import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useManuscript } from '@/context/manuscript-context';
import { throttle } from '@/lib/utils/utils';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

export function ConnectionLayer() {
    const { connections, connectionDraft, setEditingConnectionId, setActiveTab, updateMark, showAnnotations } = useManuscript();
    const isDarkMode = useDarkMode();
    const svgRef = useRef<SVGSVGElement>(null);
    const [paths, setPaths] = useState<any[]>([]);
    const containerRef = useRef<HTMLElement | null>(null);

    const calculatePaths = useCallback(() => {
        if (!svgRef.current) return;

        // On mobile, the SVG is fixed inset-0, so its top/left are 0/0 relative to the viewport.
        // We use getBoundingClientRect() for words, which are also viewport-relative.
        // However, we should double check the SVG's actual position in case of unusual layouts.
        const svgRect = svgRef.current.getBoundingClientRect();

        const newPaths = connections.map(conn => {
            const startEl = document.getElementById(conn.startWordId);
            const endEl = document.getElementById(conn.endWordId);

            if (!startEl || !endEl) return null;

            const startRect = startEl.getBoundingClientRect();
            const endRect = endEl.getBoundingClientRect();

            // Calculate anchor points (center of the word elements)
            const x1 = startRect.left - svgRect.left + startRect.width / 2;
            const y1 = startRect.top - svgRect.top + startRect.height / 2;
            const x2 = endRect.left - svgRect.left + endRect.width / 2;
            const y2 = endRect.top - svgRect.top + endRect.height / 2;

            // Simple bezier curve calculation
            const dx = x2 - x1;
            const dy = y2 - y1;
            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2;

            // Dynamic arc calculation
            const dist = Math.sqrt(dx * dx + dy * dy);

            // Factor: How much to bulge? Reduce for very long distances.
            const factor = Math.max(0.08, 0.2 - (dist / 1500) * 0.1);

            // Max offset: Prevent the curve from going off-screen (max 60px bulge)
            const offsetX = Math.max(-60, Math.min(60, -dy * factor));
            const offsetY = Math.max(-60, Math.min(60, dx * factor));

            const cx = midX + offsetX;
            const cy = midY + offsetY;

            // Calculate the midpoint of the curve (t=0.5)
            // For a quadratic bezier, P(0.5) = 0.25*P1 + 0.5*CP + 0.25*P2
            const midPX = 0.25 * x1 + 0.5 * cx + 0.25 * x2;
            const midPY = 0.25 * y1 + 0.5 * cy + 0.25 * y2;

            // The tangent at t=0.5 for a quadratic bezier is parallel to the line between end points
            let angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);

            // Normalize angle to keep text upright
            if (angle > 90) angle -= 180;
            if (angle < -90) angle += 180;

            return {
                id: conn.id,
                d: `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`,
                midX: midPX,
                midY: midPY,
                angle,
                color: 'rgba(234, 179, 8, 0.8)',
                label: conn.label,
                isDirected: conn.connectionStyleId !== 'line'
            };
        }).filter(Boolean);

        setPaths(newPaths);
    }, [connections]);

    const draftPath = useMemo(() => {
        if (!connectionDraft || !svgRef.current) return null;

        const startEl = document.getElementById(connectionDraft.startWordId);
        if (!startEl) return null;

        const svgRect = svgRef.current.getBoundingClientRect();
        const startRect = startEl.getBoundingClientRect();

        const x1 = startRect.left - svgRect.left + startRect.width / 2;
        const y1 = startRect.top - svgRect.top + startRect.height / 2;
        const x2 = connectionDraft.currentPos.x - svgRect.left;
        const y2 = connectionDraft.currentPos.y - svgRect.top;

        const dx = x2 - x1;
        const dy = y2 - y1;
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;

        const cx = midX - dy * 0.2;
        const cy = midY + dx * 0.2;

        return {
            d: `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`,
            color: 'rgba(234, 179, 8, 0.4)'
        };
    }, [connectionDraft]);

    // Use a ref to keep track of the latest calculation logic to avoid stale closures in throttled calls
    const lastCalculatePaths = useRef(calculatePaths);
    useEffect(() => {
        lastCalculatePaths.current = calculatePaths;
    }, [calculatePaths]);

    // Throttled version for performance - 100ms is a good balance for mobile
    const throttledCalculatePaths = useMemo(() => throttle(() => {
        lastCalculatePaths.current();
    }, 100), []);

    // Recalculate on window resize or when connections change
    useEffect(() => {
        // Initial immediate calculation
        calculatePaths();

        // Listen for window resize which affects layout
        window.addEventListener('resize', throttledCalculatePaths);

        return () => {
            window.removeEventListener('resize', throttledCalculatePaths);
        };
    }, [connections, throttledCalculatePaths]); // Depend on connections directly to trigger fresh calcs

    // Observe for DOM changes that might affect word positions
    useEffect(() => {
        // Initial container discovery
        const updateContainer = () => {
            const firstWord = document.querySelector('[id^="w-"]');
            if (firstWord) {
                containerRef.current = firstWord.closest('[class*="relative"]') as HTMLElement || document.body;
                return true;
            }
            return false;
        };

        updateContainer();

        const observer = new MutationObserver(() => {
            if (!containerRef.current) updateContainer();
            throttledCalculatePaths();
        });

        observer.observe(document.body, { childList: true, subtree: true });

        // Also fire one more calculation after a short delay for late-loading layouts
        const timer = setTimeout(calculatePaths, 500);

        return () => {
            observer.disconnect();
            clearTimeout(timer);
        };
    }, [throttledCalculatePaths, calculatePaths]);

    return (
        <svg
            ref={svgRef}
            className="absolute inset-0 z-30 pointer-events-none"
            style={{
                opacity: showAnnotations ? 1 : 0,
                transition: 'opacity 0.2s',
                width: '100%',
                height: '100%',
                minHeight: '100%'
            }}
        >
            <defs>
                <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                >
                    <polygon points="0 0, 10 3.5, 0 7" fill="rgba(234, 179, 8, 0.8)" />
                </marker>

                {/* Filter for a slightly rough, hand-drawn look (Pencil texture) */}
                <filter id="pencil-texture" x="-20%" y="-20%" width="140%" height="140%">
                    <feTurbulence type="fractalNoise" baseFrequency="0.03" numOctaves="3" seed="1" result="noise" />
                    <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" xChannelSelector="R" yChannelSelector="G" />
                </filter>
            </defs>
            {paths.map(path => (
                <g key={path.id} className="cursor-pointer pointer-events-auto" onClick={(e) => {
                    e.stopPropagation();
                    const conn = connections.find(c => c.id === path.id);
                    if (conn) {
                        setEditingConnectionId(conn.id);
                        setActiveTab('connect');
                    }
                }}>
                    {/* Thick invisible hit area for easier clicking */}
                    <path
                        d={path.d}
                        stroke="transparent"
                        strokeWidth="20"
                        fill="none"
                    />
                    <path
                        id={`path-${path.id}`}
                        d={path.d}
                        stroke={path.color}
                        strokeWidth="2"
                        fill="none"
                        markerEnd={path.isDirected ? "url(#arrowhead)" : undefined}
                        filter="url(#pencil-texture)"
                    />
                    {path.label && (
                        <g
                            transform={`translate(${path.midX}, ${path.midY}) rotate(${path.angle})`}
                            className="select-none pointer-events-none"
                        >
                            <foreignObject
                                x={-(Math.max(path.label.length, 1) * 3.8 + 12)}
                                y="-12"
                                width={Math.max(path.label.length, 1) * 7.6 + 24}
                                height="24"
                                className="overflow-visible"
                            >
                                <div className={`
                                    w-full h-full flex items-center justify-center 
                                    rounded-full border shadow-sm backdrop-blur-md
                                    ${isDarkMode
                                        ? 'bg-gray-900/40 border-white/10 text-gray-100'
                                        : 'bg-white/40 border-black/5 text-gray-900'}
                                `}>
                                    <span className="text-[10px] font-normal italic px-2 whitespace-nowrap tracking-tight leading-none">
                                        {path.label}
                                    </span>
                                </div>
                            </foreignObject>
                        </g>
                    )}
                </g>
            ))}
            {draftPath && (
                <path
                    d={draftPath.d}
                    stroke={draftPath.color}
                    strokeWidth="2"
                    strokeDasharray="4 4"
                    fill="none"
                    markerEnd="url(#arrowhead)"
                    filter="url(#pencil-texture)"
                />
            )}
        </svg>
    );
}
